/* eslint-disable @next/next/no-img-element --
   Same reasoning as DirectoryCard: the map is a Google Static Maps URL
   requested at the size it renders at, so next/image has nothing to optimize
   and would only add a third-party remotePattern plus an optimizer hop. */
import EventIcon from '@mui/icons-material/Event'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import React from 'react'
import ClaimFacility from 'src/components/DirectoryFacility/ClaimFacility'
import DirectoryDisclaimer from 'src/components/DirectoryFacility/DirectoryDisclaimer'
import OnDemandFichaCard, {
  resolveFichaOnDemand
} from 'src/components/DirectoryFacility/OnDemandFichaCard'
import RequestRemoval from 'src/components/DirectoryFacility/RequestRemoval'
import RevealContact from 'src/components/DirectoryFacility/RevealContact'
import { getStaticMapUrl } from 'src/components/Map/staticMapUrl'
import { DIRECTORY_CLAIM_CALL_URL, SITE_ORIGIN } from 'src/constants/site'
import { ILocation } from 'src/types/graphql'
import { formatTimestampMonthDayYear, numberWithCommas } from 'src/utils'
import {
  DirectoryCategory,
  ISibling,
  buildVerifiedNearbyHref,
  directoryBrandColor,
  directoryCityName,
  directoryMonogram,
  displayableDirectoryHours,
  sortDirectorySizes
} from 'src/utils/directory'
import { prettifyDirectoryText } from 'src/utils/directoryText'
import { IVA_RATE } from 'src/utils/iva'
import {
  trackDirectoryHostContactClick,
  trackDirectoryMapOpen,
  trackDirectoryRecoveryClick
} from 'src/utils/pixels/directoryEvents'
import { buildWhatsappUrl } from 'src/utils/whatsapp'

/** ink-200 — the neutral tile for a facility with no scraped brand colour. */
const DEFAULT_BRAND_COLOR = '#4F6A7E'
const MAP_WIDTH = 640
const MAP_HEIGHT = 400
const MAP_ZOOM = 16

export interface FichaProps {
  location: ILocation
  category: DirectoryCategory
  citySlug: string
  /** Canonical path of this ficha — the return URL for the claim login gate. */
  fichaPath: string
  /** Other indexable fichas in this city — see `RelatedFichas` below. */
  siblings: ISibling[]
  /**
   * Whether the facility's area has ANY verified SpotMe listing (server-side
   * count). In a zero-supply city "Ver espacios verificados" would land on a
   * page with none, so every verified-supply ramp on this page swaps to the
   * corporate WhatsApp instead. Defaults to true on an unknown answer — the
   * ramps that existed before stay the fallback.
   */
  hasVerifiedSupply: boolean
}

/**
 * A directory ficha (plan §2.3) — the public page for a real, UNCLAIMED
 * facility.
 *
 * What is ungated: identity, map, address, hours, and whatever sizes/prices we
 * captured, each carrying its own attribution. What is gated: the contact
 * bundle, behind the one-field reveal and the facility's monthly cap. The
 * facility's WEBSITE is inside the gated bundle on purpose — the operator's
 * own site carries their phone, so ungating it would make the cap decorative.
 *
 * Every price on this page has already passed the 45-day staleness rule in the
 * server's `directoryMeta` resolver, which nulls `priceFromMxn` past the
 * window. There is deliberately no second staleness check here: one rule, one
 * place.
 */
const Ficha = ({
  location,
  category,
  citySlug,
  fichaPath,
  siblings,
  hasVerifiedSupply
}: FichaProps) => {
  const { t } = useTranslation('directorio')

  const {
    id,
    title,
    address,
    coordinates,
    directoryMeta,
    directoryHasContact,
    directorySources,
    priceSource,
    priceCapturedAt
  } = location

  // DENUE capitals and registry filler cleaned for display (directoryText.ts).
  const name = prettifyDirectoryText(title)
  const displayAddress = prettifyDirectoryText(address)
  const cityName = directoryCityName(citySlug)
  const brandColor = directoryBrandColor(location) ?? DEFAULT_BRAND_COLOR
  const monogram = directoryMonogram(location)
  const verifiedNearbyHref = buildVerifiedNearbyHref(
    category,
    citySlug,
    coordinates
  )
  const verifiedRampHref = hasVerifiedSupply ? verifiedNearbyHref : null
  // On-demand cross-sell (storage fichas in covered markets only). Gated by
  // the facility's own coordinates — see resolveFichaOnDemand for the rules
  // and for why this surface exists on fichas but NOT on host listings.
  const onDemandResolved = resolveFichaOnDemand(category, citySlug, coordinates)
  const mapUrl = coordinates
    ? getStaticMapUrl({
        center: coordinates,
        zoom: MAP_ZOOM,
        white: true,
        width: MAP_WIDTH,
        height: MAP_HEIGHT
      })
    : null
  const googleMapsHref = coordinates
    ? `https://www.google.com/maps/search/?api=1&query=${coordinates.lat},${coordinates.lng}`
    : null

  const capturedDate = formatTimestampMonthDayYear(priceCapturedAt)
  // Scraped hours are frequently page noise, sometimes carrying a price — see
  // `displayableDirectoryHours`. Rejecting is the safe default.
  const displayableHours = displayableDirectoryHours(directoryMeta?.hours)
  const hasPublishedFigures = !!(
    directoryMeta?.sizes || directoryMeta?.priceFromMxn
  )
  const categoryShortLabel =
    category === DirectoryCategory.Storage
      ? t('CATEGORY_SHORT.bodegas')
      : t('CATEGORY_SHORT.estacionamientos')

  return (
    <main className="bg-cream-100 pb-16">
      <div className="mx-auto w-full max-w-280 px-4 pt-[calc(var(--nav-h)+16px)] md:px-6 md:pt-[calc(var(--nav-h)+32px)]">
        {/* The parent crumb is the VERIFIED supply page for this metro +
            category, not a directory index — there isn't one any more. A
            renter backing out of a ficha should land on spaces they can
            actually book. */}
        <nav aria-label={t('FICHA.A11Y_BREADCRUMB')}>
          <ol className="flex flex-wrap items-center gap-x-2 gap-y-2 text-[11px] text-ink-200 md:text-xs">
            <li>
              <Link
                href={verifiedNearbyHref}
                className="inline-block py-1 hover:text-ink-700"
              >
                {t('FICHA.BREADCRUMB_VERIFIED', {
                  category: categoryShortLabel,
                  city: cityName
                })}
              </Link>
            </li>
            {/* The separator is a pseudo-element, not its own <li>: an empty
                list item inflates the count a screen reader announces. */}
            <li
              aria-current="page"
              className="text-ink-700 before:mr-2 before:text-ink-200 before:content-['/']"
            >
              {t('CHIP')}
            </li>
          </ol>
        </nav>

        {/* Header. The brand tile overlaps the title block's top edge at md+,
            which is the page's one piece of layering — a flat stack of
            sections would read as a form, not a place. */}
        <header className="mt-5 flex flex-col gap-4 md:mt-8 md:flex-row md:items-start md:gap-6">
          <svg
            viewBox="0 0 36 36"
            className="size-14 shrink-0 md:size-20"
            aria-hidden="true"
            focusable="false"
          >
            <rect width="36" height="36" rx="9" fill={brandColor} />
            <text
              x="18"
              y="18"
              textAnchor="middle"
              dominantBaseline="central"
              fill="#FFFFFF"
              fontSize="16"
              fontWeight="500"
            >
              {monogram}
            </text>
          </svg>

          <div className="min-w-0 flex-1">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-ink-100/10 px-2.5 py-1 text-[10px] font-medium tracking-[0.06em] text-ink-200 uppercase md:text-[11px]">
              <i className="si si-bulb text-[9px]" aria-hidden="true" />
              {t('CHIP')}
            </span>
            <h1 className="mt-2 font-display text-[26px] leading-[1.1] font-medium text-ink-700 md:text-[38px]">
              {name}
            </h1>
            {directoryMeta?.chain && (
              <p className="mt-1 text-[13px] text-ink-200 md:text-sm">
                {directoryMeta.chain}
              </p>
            )}
          </div>
        </header>

        <div className="mt-6 md:mt-8">
          <DirectoryDisclaimer
            locationId={id}
            verifiedNearbyHref={verifiedRampHref}
          />
        </div>

        <div className="mt-8 grid grid-cols-1 gap-8 md:mt-10 md:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] md:gap-10">
          <div className="flex flex-col gap-8">
            <section>
              <h2 className="font-display text-[19px] leading-tight font-medium text-ink-700 md:text-xl">
                {t('FICHA.ADDRESS_TITLE')}
              </h2>
              <p className="mt-2 text-[15px] leading-relaxed text-ink-700">
                {displayAddress}
              </p>
              {/* The map IS the link (2026-09-26): 169 people in 30 days tapped
                  the static map and nothing happened. One anchor wraps map and
                  label — one destination, one accessible name, one event split
                  by `part`. The href stays the ungated coordinate pin, never a
                  search for the business's own Google listing: that listing
                  shows the phone the reveal gates behind its monthly cap. */}
              {googleMapsHref && (
                <a
                  href={googleMapsHref}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  onClick={(e) =>
                    trackDirectoryMapOpen({
                      locationId: id,
                      part:
                        e.target instanceof Element && e.target.closest('img')
                          ? 'map'
                          : 'label'
                    })
                  }
                  className="group mt-4 block rounded-2xl focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ink-700"
                >
                  {mapUrl && (
                    <span className="relative block overflow-hidden rounded-2xl border border-ink-100/15 transition-colors duration-200 group-hover:border-ink-100/40">
                      <img
                        src={mapUrl}
                        alt={t('FICHA.MAP_ALT', { name })}
                        width={MAP_WIDTH}
                        height={MAP_HEIGHT}
                        loading="lazy"
                        decoding="async"
                        className="h-auto w-full transition-transform duration-300 group-hover:scale-[1.02] motion-reduce:transition-none"
                      />
                      <span
                        aria-hidden="true"
                        className="absolute top-3 right-3 flex size-9 items-center justify-center rounded-full border border-ink-100/15 bg-white-500 text-blue-800 transition-transform duration-200 group-hover:-translate-y-0.5 group-active:scale-95 motion-reduce:transition-none"
                      >
                        <OpenInNewIcon className="text-[18px]!" />
                      </span>
                    </span>
                  )}
                  <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-blue-800 group-hover:underline">
                    {t('FICHA.MAP_LINK')}
                    <i className="si si-right text-[8px]" aria-hidden="true" />
                    <span className="sr-only">{t('FICHA.MAP_NEW_TAB')}</span>
                  </span>
                </a>
              )}
            </section>

            {displayableHours && (
              <section>
                <h2 className="font-display text-[19px] leading-tight font-medium text-ink-700 md:text-xl">
                  {t('FICHA.HOURS_TITLE')}
                </h2>
                <p className="mt-2 text-[15px] leading-relaxed text-ink-700">
                  {displayableHours}
                </p>
              </section>
            )}

            {hasPublishedFigures && (
              <section>
                <h2 className="font-display text-[19px] leading-tight font-medium text-ink-700 md:text-xl">
                  {t('FICHA.SIZES_TITLE')}
                </h2>
                <dl className="mt-3 flex flex-col gap-2">
                  {directoryMeta?.sizes && (
                    <div className="flex flex-col gap-0.5 md:flex-row md:items-baseline md:gap-3">
                      <dt className="shrink-0 text-[11px] tracking-[0.04em] text-ink-200 uppercase md:w-28">
                        {t('FICHA.SIZES_LABEL')}
                      </dt>
                      <dd className="text-[15px] text-ink-700">
                        {sortDirectorySizes(directoryMeta.sizes)}
                      </dd>
                    </div>
                  )}
                  {!!directoryMeta?.priceFromMxn && (
                    <div className="flex flex-col gap-0.5 md:flex-row md:items-baseline md:gap-3">
                      <dt className="shrink-0 text-[11px] tracking-[0.04em] text-ink-200 uppercase md:w-28">
                        {t('FICHA.PRICE_LABEL')}
                      </dt>
                      <dd className="text-[15px] text-ink-700">
                        {t('FICHA.PRICE_VALUE', {
                          price: numberWithCommas(directoryMeta.priceFromMxn)
                        })}
                        {/* Same «+ IVA = $X» convention as a Traditional
                            listing, and only when the operator's own site
                            says its price is before IVA. */}
                        {directoryMeta.priceExcludesIva && (
                          <span className="block text-[12px] text-ink-300">
                            {t('FICHA.PRICE_PLUS_IVA', {
                              total: numberWithCommas(
                                Math.ceil(
                                  directoryMeta.priceFromMxn * (1 + IVA_RATE)
                                )
                              )
                            })}
                          </span>
                        )}
                      </dd>
                    </div>
                  )}
                </dl>
                {/* Attribution is not optional garnish: a price we scraped and
                    render unattributed becomes a SpotMe price claim. */}
                <p className="mt-3 text-[11px] leading-snug text-ink-200">
                  {capturedDate
                    ? t('FICHA.ATTRIBUTION', {
                        source: priceSource || t('FICHA.SOURCES_FALLBACK'),
                        date: capturedDate
                      })
                    : t('FICHA.ATTRIBUTION_NO_DATE', {
                        source: priceSource || t('FICHA.SOURCES_FALLBACK')
                      })}
                </p>
              </section>
            )}

            <p className="text-[11px] leading-snug text-ink-200">
              {t('FICHA.SOURCES', {
                sources:
                  directorySources?.join(' · ') || t('FICHA.SOURCES_FALLBACK')
              })}
            </p>
          </div>

          {/* Right rail. Sticky from md up — the reveal is the one action on
              the page and should stay reachable while the renter reads. */}
          <div className="flex flex-col gap-6 md:sticky md:top-[calc(var(--nav-h)+1.5rem)] md:self-start">
            {directoryHasContact ? (
              <RevealContact
                locationId={id}
                verifiedNearbyHref={verifiedRampHref}
                coordinates={coordinates}
                surface="ficha"
              />
            ) : (
              <section className="rounded-2xl border border-ink-100/15 bg-cream-300 p-5 md:p-6">
                <h2 className="font-display text-[19px] leading-tight font-medium text-ink-700 md:text-xl">
                  {t('REVEAL.NO_CONTACT_TITLE')}
                </h2>
                <p className="mt-2 text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
                  {t('REVEAL.NO_CONTACT_BODY')}
                </p>
              </section>
            )}

            {verifiedRampHref ? (
              <section className="rounded-2xl border border-blue-500/20 bg-blue-500/5 p-5 md:p-6">
                <h2 className="font-display text-[19px] leading-tight font-medium text-ink-700 md:text-xl">
                  {t('FICHA.NEARBY_TITLE')}
                </h2>
                <p className="mt-2 text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
                  {t('FICHA.NEARBY_BODY')}
                </p>
                <Link
                  href={verifiedRampHref}
                  onClick={() =>
                    trackDirectoryRecoveryClick({
                      locationId: id,
                      target: 'verified_nearby'
                    })
                  }
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-blue-800 px-5 py-2.5 text-sm font-medium text-white-500 transition-transform duration-200 hover:translate-x-0.5"
                >
                  {t('FICHA.NEARBY_CTA')}
                  <i className="si si-right text-[9px]" aria-hidden="true" />
                </Link>
              </section>
            ) : (
              // Zero-supply area: no verified page to send anyone to. The
              // corporate line (LangGraph triage → HubSpot → Fernando) can
              // still help — and an unserved renter here is exactly the
              // demand signal the supply team recruits with.
              <section className="rounded-2xl border border-blue-500/20 bg-blue-500/5 p-5 md:p-6">
                <h2 className="font-display text-[19px] leading-tight font-medium text-ink-700 md:text-xl">
                  {t('FICHA.NO_SUPPLY_TITLE')}
                </h2>
                <p className="mt-2 text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
                  {t('FICHA.NO_SUPPLY_BODY')}
                </p>
                <a
                  href={buildWhatsappUrl(
                    t('FICHA.NO_SUPPLY_WA_MESSAGE', { city: cityName })
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    trackDirectoryRecoveryClick({
                      locationId: id,
                      target: 'whatsapp_no_supply'
                    })
                  }
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-blue-800 px-5 py-2.5 text-sm font-medium text-white-500 transition-transform duration-200 hover:translate-x-0.5"
                >
                  <WhatsAppIcon className="text-[18px]!" aria-hidden="true" />
                  {t('FICHA.NO_SUPPLY_CTA')}
                </a>
              </section>
            )}

            {/* Second recovery ramp: verified nearby is "book a space", this
                is "never move a box". Below NEARBY on purpose — verified
                supply converts on-platform and keeps priority. */}
            {onDemandResolved && (
              <OnDemandFichaCard resolved={onDemandResolved} />
            )}
          </div>
        </div>

        {/* Other fichas in this city — the CRAWL PATH into the directory
            cluster, and the fix for the 168 orphan pages the 2026-08-28 Ahrefs
            audit flagged (see `fetchSiblings` in the page for the full why).
            Plain server-rendered anchors: no `dynamic()`, no viewport gate, no
            client fetch — everything that makes the tier floor inside search
            invisible to a crawler is deliberately absent here. Sits ABOVE the
            claim block because it serves the renter, and the claim block is
            the owner's lane. */}
        {siblings.length > 0 && (
          <section
            className="mt-12 border-t border-ink-100/15 pt-8 md:mt-16"
            aria-labelledby="ficha-related-heading"
            data-testid="ficha-related"
          >
            <h2
              id="ficha-related-heading"
              className="font-display text-[19px] leading-tight font-medium text-ink-700 md:text-xl"
            >
              {t('FICHA.RELATED_TITLE', { city: cityName })}
            </h2>
            <p className="mt-1.5 max-w-140 text-[14px] leading-relaxed text-ink-200">
              {t('FICHA.RELATED_BODY')}
            </p>
            {/* A list, not a card grid: this is a related-links block, not the
                browsable directory index that was built and deliberately
                removed 2026-08-23. Two columns from md purely to keep six
                links from running as a long ribbon on desktop. */}
            <ul className="mt-4 grid gap-x-8 gap-y-1 md:grid-cols-2">
              {siblings.map((sibling) => (
                <li key={sibling.href}>
                  <Link
                    href={sibling.href}
                    className="group -mx-2 flex items-baseline gap-2 rounded-lg px-2 py-1.5 transition-colors duration-200 hover:bg-cream-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-700"
                  >
                    <span className="min-w-0 flex-1 truncate text-[14px] font-medium text-ink-700 underline-offset-2 group-hover:underline">
                      {prettifyDirectoryText(sibling.name)}
                    </span>
                    {sibling.area && (
                      <span className="shrink-0 text-[12px] text-ink-200">
                        {sibling.area}
                      </span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Claim + removal. Both belong to the facility owner, so they sit
            together at the end, away from the renter's path. */}
        <section className="mt-12 border-t border-ink-100/15 pt-8 md:mt-16">
          {/* `md:flex-wrap` + `md:contents` on the ClaimFacility wrapper: the
              CTA and its expanding panel become DIRECT flex items, so the
              header + CTA share the first row while the open panel (which
              carries `md:basis-full`) wraps to its own full-width line.
              Keeping the panel inside a `md:w-auto` column rendered the whole
              claim flow at button width on desktop (caught live 2026-08-24). */}
          <div className="flex flex-col gap-4 md:flex-row md:flex-wrap md:items-center md:justify-between md:gap-x-8 md:gap-y-0">
            <div className="max-w-140">
              <h2 className="font-display text-[19px] leading-tight font-medium text-ink-700 md:text-xl">
                {t('FICHA.CLAIM_TITLE')}
              </h2>
              <p className="mt-1.5 text-[14px] leading-relaxed text-ink-200">
                {t('FICHA.CLAIM_BODY')}
              </p>
              <Link
                href="/acerca-de-spotme/anfitrion/directorio-de-negocios"
                className="mt-1.5 inline-block text-[13px] text-blue-500 underline"
              >
                {t('FICHA.CLAIM_HELP_LINK')}
              </Link>
              {/* Low-friction host-lead shortcuts (Jules, 2026-08-28): an
                  owner who won't fill a form still converts on a WhatsApp
                  draft or a booked call. The WA draft names the facility and
                  carries the ficha URL so the corporate line (LangGraph
                  triage → HubSpot → specialist) opens with full context —
                  it MUST stay on `buildWhatsappUrl`'s default corporate
                  number, never a specialist's phone (src/utils/whatsapp.ts).
                  Observed-only `directory_host_contact_click`; the claim
                  form stays the primary CTA on the right. */}
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px]">
                <span className="text-ink-200">
                  {t('FICHA.CLAIM_ALT_LEAD')}
                </span>
                <a
                  href={buildWhatsappUrl(
                    t('FICHA.CLAIM_WA_MESSAGE', {
                      name,
                      url: `${SITE_ORIGIN}${fichaPath}`
                    })
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    trackDirectoryHostContactClick({
                      locationId: id,
                      channel: 'whatsapp'
                    })
                  }
                  className="inline-flex items-center gap-1.5 font-medium text-blue-500 underline-offset-2 hover:underline"
                >
                  <WhatsAppIcon className="text-[16px]" aria-hidden="true" />
                  {t('FICHA.CLAIM_WA_CTA')}
                </a>
                <a
                  href={DIRECTORY_CLAIM_CALL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    trackDirectoryHostContactClick({
                      locationId: id,
                      channel: 'meeting'
                    })
                  }
                  className="inline-flex items-center gap-1.5 font-medium text-blue-500 underline-offset-2 hover:underline"
                >
                  <EventIcon className="text-[16px]" aria-hidden="true" />
                  {t('FICHA.CLAIM_MEET_CTA')}
                </a>
              </div>
            </div>
            {/* The CTA expands into the real claim flow in place. It used to
                link to /anfitrion, which started a NEW listing — the exact
                second face for one facility that the whole in-place claim
                design exists to prevent. */}
            <div className="w-full md:contents">
              <ClaimFacility
                locationId={id}
                fichaPath={fichaPath}
                category={category}
              />
            </div>
          </div>
          {/* Removal is a REQUEST, not a link: the one-click token is only ever
              delivered to the facility's own on-record contact, or anyone could
              delist a competitor. */}
          <RequestRemoval locationId={id} facilityName={name} />
        </section>
      </div>
    </main>
  )
}

export default React.memo(Ficha)
